import { useState, useRef, useEffect } from "react";
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Grid,
  Icon,
  Badge,
  Separator,
  Switch,
  SimpleGrid,
  Flex,
} from "@chakra-ui/react";
import {
  FaPython,
  FaJs,
  FaReact,
  FaCode,
  FaRocket,
  FaCheckCircle,
  FaUserFriends,
  FaChalkboardTeacher,
  FaSchool,
  FaGraduationCap,
  FaStar,
  FaCrown,
  FaArrowRight,
  FaUsers,
  FaHandHoldingHeart,
  FaEnvelope,
} from "react-icons/fa";
import { useColorModeValue } from "../ui/color-mode";
import { useAccessibility } from "../../context/AccessibilityContext";
import navigationSound from "../../assets/sounds/navigation.mp3";
import {
  languageSubscriptions,
  curriculumBundles,
  mentoringPlans,
  groupRates,
  scholarshipProgram,
} from "../../data/subscriptionData";

const iconMap = {
  FaPython,
  FaJs,
  FaReact,
  FaCode,
  FaRocket,
  FaUserFriends,
  FaChalkboardTeacher,
  FaSchool,
  FaGraduationCap,
};

export const PricingScreen = ({ onBack, onSelectPlan }) => {
  const [isAnnual, setIsAnnual] = useState(true);
  const [activeTab, setActiveTab] = useState("bundles");
  const { soundEnabled } = useAccessibility();
  const headingRef = useRef(null);

  const cardBg = useColorModeValue("white", "gray.800");
  const headingColor = useColorModeValue("gray.800", "white");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const screenBg = useColorModeValue("gray.50", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const accentGradient = "linear(to-r, #667EEA, #764BA2)";
  const tabBg = useColorModeValue("gray.100", "gray.700");
  const tabActiveBg = useColorModeValue("white", "gray.600");
  const popularBorder = useColorModeValue("purple.400", "purple.300");
  const savingsBg = useColorModeValue("green.50", "green.900");
  const savingsColor = useColorModeValue("green.700", "green.200");
  const scholarshipBg = useColorModeValue("orange.50", "orange.900");
  const scholarshipBorder = useColorModeValue("orange.200", "orange.700");
  const groupBg = useColorModeValue("blue.50", "blue.900");
  const groupBorder = useColorModeValue("blue.200", "blue.700");
  const a11yHighlightBg = useColorModeValue("purple.50", "purple.900");
  const a11yHighlightBorder = useColorModeValue("purple.200", "purple.700");

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const playSound = (sound) => {
    if (soundEnabled) {
      new Audio(sound).play();
    }
  };

  const tabs = [
    { id: "bundles", label: "Curriculum Bundles", icon: FaCode },
    { id: "individual", label: "Individual Courses", icon: FaStar },
    { id: "mentoring", label: "1-on-1 Mentoring", icon: FaUserFriends },
    { id: "groups", label: "Groups & Districts", icon: FaSchool },
    { id: "scholarships", label: "Scholarships", icon: FaGraduationCap },
  ];

  const getPrice = (plan) => {
    return isAnnual
      ? (plan.annualPrice / 12).toFixed(2)
      : plan.monthlyPrice.toFixed(2);
  };

  const getTotalPrice = (plan) => {
    return isAnnual ? plan.annualPrice.toFixed(2) : plan.monthlyPrice.toFixed(2);
  };

  // ──────────────────────────── Tab Navigation ────────────────────
  const handleTabKeyDown = (e, idx) => {
    const len = tabs.length;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      const nextIdx = (idx + 1) % len;
      setActiveTab(tabs[nextIdx].id);
      document.getElementById(`pricing-tab-${tabs[nextIdx].id}`)?.focus();
      playSound(navigationSound);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      const prevIdx = (idx - 1 + len) % len;
      setActiveTab(tabs[prevIdx].id);
      document.getElementById(`pricing-tab-${tabs[prevIdx].id}`)?.focus();
      playSound(navigationSound);
    }
  };

  // ───────────────────────── Render: Bundles ──────────────────────
  const renderBundles = () => (
    <VStack spacing={8} width="100%" align="stretch">
      <Box textAlign="center">
        <Heading size="lg" color={headingColor} mb={2}>
          Accessibility-First Curriculum Bundles
        </Heading>
        <Text color={textColor} maxW="700px" mx="auto">
          Bundles designed specifically for visually impaired learners. Start
          with text-based languages and build real, accessible websites.
        </Text>
      </Box>

      <Grid
        templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }}
        gap={8}
        width="100%"
      >
        {curriculumBundles.map((bundle) => (
          <Box
            key={bundle.id}
            bg={cardBg}
            borderRadius="2xl"
            borderWidth="2px"
            borderColor={bundle.popular ? popularBorder : borderColor}
            overflow="hidden"
            position="relative"
            transition="all 0.3s ease"
            _hover={{ transform: "translateY(-6px)", boxShadow: "2xl" }}
            role="article"
            aria-label={`${bundle.title} - ${
              isAnnual
                ? `$${(bundle.annualPrice / 12).toFixed(2)} per month billed annually`
                : `$${bundle.monthlyPrice} per month`
            }`}
          >
            {bundle.popular && (
              <Box
                bgGradient={accentGradient}
                color="white"
                textAlign="center"
                py={2}
                fontWeight="bold"
                fontSize="sm"
                letterSpacing="wide"
              >
                <HStack justify="center" gap={2}>
                  <Icon as={FaCrown} aria-hidden="true" />
                  <Text>MOST POPULAR — BEST VALUE</Text>
                </HStack>
              </Box>
            )}

            <Box p={8}>
              <VStack align="stretch" spacing={5}>
                <HStack gap={4}>
                  <Box
                    w="14"
                    h="14"
                    borderRadius="xl"
                    bgGradient={bundle.gradient}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon
                      as={iconMap[bundle.icon]}
                      boxSize={7}
                      color="white"
                      aria-hidden="true"
                    />
                  </Box>
                  <Box>
                    <Heading size="md" color={headingColor}>
                      {bundle.title}
                    </Heading>
                    <Text fontSize="sm" color={mutedText} fontWeight="medium">
                      {bundle.subtitle}
                    </Text>
                  </Box>
                </HStack>

                <Text color={textColor} fontSize="md">
                  {bundle.description}
                </Text>

                {/* Price */}
                <Box
                  bg={savingsBg}
                  borderRadius="xl"
                  p={5}
                  textAlign="center"
                >
                  <HStack justify="center" align="baseline" gap={1}>
                    <Text
                      fontSize="4xl"
                      fontWeight="extrabold"
                      color={headingColor}
                    >
                      ${getPrice(bundle)}
                    </Text>
                    <Text color={mutedText} fontSize="md">
                      /month
                    </Text>
                  </HStack>
                  {isAnnual && (
                    <Text fontSize="sm" color={savingsColor} fontWeight="bold">
                      Billed ${bundle.annualPrice}/year — {bundle.savings}
                    </Text>
                  )}
                </Box>

                {/* Courses Included */}
                <Box>
                  <Text
                    fontWeight="bold"
                    color={headingColor}
                    fontSize="sm"
                    mb={2}
                  >
                    Courses Included:
                  </Text>
                  <HStack gap={2} flexWrap="wrap">
                    {bundle.courses.map((c) => (
                      <Badge
                        key={c}
                        colorScheme="purple"
                        variant="subtle"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="xs"
                        textTransform="uppercase"
                      >
                        {c}
                      </Badge>
                    ))}
                  </HStack>
                </Box>

                {/* Features */}
                <VStack align="stretch" spacing={2}>
                  <Text
                    fontWeight="bold"
                    color={headingColor}
                    fontSize="sm"
                  >
                    What You Get:
                  </Text>
                  {bundle.features.map((f, i) => (
                    <HStack key={i} gap={2} align="start">
                      <Icon
                        as={FaCheckCircle}
                        color="green.400"
                        boxSize={4}
                        mt={1}
                        aria-hidden="true"
                        flexShrink={0}
                      />
                      <Text fontSize="sm" color={textColor}>
                        {f}
                      </Text>
                    </HStack>
                  ))}
                </VStack>

                {/* Accessibility Highlights */}
                <Box
                  bg={a11yHighlightBg}
                  borderWidth="1px"
                  borderColor={a11yHighlightBorder}
                  borderRadius="lg"
                  p={4}
                >
                  <Text
                    fontWeight="bold"
                    fontSize="sm"
                    color={headingColor}
                    mb={2}
                  >
                    ♿ Accessibility Highlights:
                  </Text>
                  {bundle.accessibilityHighlights.map((h, i) => (
                    <HStack key={i} gap={2} align="start" mb={1}>
                      <Icon
                        as={FaStar}
                        color="purple.400"
                        boxSize={3}
                        mt={1}
                        aria-hidden="true"
                        flexShrink={0}
                      />
                      <Text fontSize="xs" color={textColor}>
                        {h}
                      </Text>
                    </HStack>
                  ))}
                </Box>

                {/* Target Audience */}
                <Text fontSize="xs" fontStyle="italic" color={mutedText}>
                  {bundle.targetAudience}
                </Text>

                <Button
                  size="lg"
                  width="100%"
                  bgGradient={bundle.gradient}
                  color="white"
                  _hover={{ opacity: 0.9, transform: "scale(1.02)" }}
                  transition="all 0.2s"
                  rightIcon={<FaArrowRight />}
                  onClick={() => {
                    playSound(navigationSound);
                    onSelectPlan?.(bundle.id);
                  }}
                  aria-label={`Subscribe to ${bundle.title} for $${getPrice(
                    bundle
                  )} per month`}
                >
                  Get Started
                </Button>
              </VStack>
            </Box>
          </Box>
        ))}
      </Grid>
    </VStack>
  );

  // ────────────────────── Render: Individual Courses ──────────────
  const renderIndividual = () => (
    <VStack spacing={8} width="100%" align="stretch">
      <Box textAlign="center">
        <Heading size="lg" color={headingColor} mb={2}>
          Individual Language Subscriptions
        </Heading>
        <Text color={textColor} maxW="600px" mx="auto">
          Pick a single language to learn. Python is our most affordable entry
          point — perfect for beginners.
        </Text>
      </Box>

      <Grid
        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        gap={6}
        width="100%"
      >
        {languageSubscriptions.map((plan) => (
          <Box
            key={plan.id}
            bg={cardBg}
            borderRadius="2xl"
            borderWidth="2px"
            borderColor={borderColor}
            overflow="hidden"
            transition="all 0.3s ease"
            _hover={{ transform: "translateY(-4px)", boxShadow: "xl" }}
            role="article"
            aria-label={`${plan.label} - $${getPrice(plan)} per month`}
          >
            {/* Color header bar */}
            <Box h="4px" bg={plan.color} />

            <Box p={7}>
              <VStack align="stretch" spacing={4}>
                <HStack gap={3}>
                  <Icon
                    as={iconMap[plan.icon]}
                    boxSize={8}
                    color={plan.color}
                    aria-hidden="true"
                  />
                  <Box>
                    <Heading size="sm" color={headingColor}>
                      {plan.label}
                    </Heading>
                    <Text fontSize="xs" color={mutedText}>
                      {plan.tagline}
                    </Text>
                  </Box>
                </HStack>

                <Badge
                  colorScheme={
                    plan.tier === "starter"
                      ? "green"
                      : plan.tier === "pro"
                      ? "blue"
                      : "purple"
                  }
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="xs"
                  textTransform="uppercase"
                  width="fit-content"
                >
                  {plan.tier}
                </Badge>

                {/* Price */}
                <Box textAlign="center" py={3}>
                  <HStack justify="center" align="baseline" gap={1}>
                    <Text
                      fontSize="3xl"
                      fontWeight="extrabold"
                      color={headingColor}
                    >
                      ${getPrice(plan)}
                    </Text>
                    <Text color={mutedText} fontSize="sm">
                      /mo
                    </Text>
                  </HStack>
                  {isAnnual && (
                    <Text fontSize="xs" color={savingsColor}>
                      ${getTotalPrice(plan)}/year
                    </Text>
                  )}
                </Box>

                <Separator />

                {/* Features */}
                <VStack align="stretch" spacing={2}>
                  {plan.features.map((f, i) => (
                    <HStack key={i} gap={2} align="start">
                      <Icon
                        as={FaCheckCircle}
                        color="green.400"
                        boxSize={3}
                        mt={1}
                        aria-hidden="true"
                        flexShrink={0}
                      />
                      <Text fontSize="xs" color={textColor}>
                        {f}
                      </Text>
                    </HStack>
                  ))}
                </VStack>

                {/* Accessibility Note */}
                <Text fontSize="xs" fontStyle="italic" color={mutedText} mt={2}>
                  {plan.accessibilityNote}
                </Text>

                <Button
                  size="md"
                  width="100%"
                  colorScheme="blue"
                  variant={plan.tier === "starter" ? "solid" : "outline"}
                  onClick={() => {
                    playSound(navigationSound);
                    onSelectPlan?.(plan.id);
                  }}
                  aria-label={`Subscribe to ${plan.label}`}
                >
                  Choose {plan.language}
                </Button>
              </VStack>
            </Box>
          </Box>
        ))}
      </Grid>
    </VStack>
  );

  // ─────────────────────── Render: Mentoring ─────────────────────
  const renderMentoring = () => (
    <VStack spacing={8} width="100%" align="stretch">
      <Box textAlign="center">
        <Heading size="lg" color={headingColor} mb={2}>
          1-on-1 Mentoring Subscriptions
        </Heading>
        <Text color={textColor} maxW="650px" mx="auto">
          Work with accessibility-trained mentors who understand screen reader
          workflows and assistive technology.
        </Text>
      </Box>

      <Grid
        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
        gap={8}
        width="100%"
        maxW="900px"
        mx="auto"
      >
        {mentoringPlans.map((plan) => (
          <Box
            key={plan.id}
            bg={cardBg}
            borderRadius="2xl"
            borderWidth="2px"
            borderColor={plan.popular ? popularBorder : borderColor}
            overflow="hidden"
            position="relative"
            transition="all 0.3s ease"
            _hover={{ transform: "translateY(-4px)", boxShadow: "xl" }}
            role="article"
            aria-label={`${plan.title} - $${getPrice(plan)} per month`}
          >
            {plan.popular && (
              <Box
                bg="purple.500"
                color="white"
                textAlign="center"
                py={2}
                fontWeight="bold"
                fontSize="sm"
              >
                ⭐ RECOMMENDED
              </Box>
            )}

            <Box p={7}>
              <VStack align="stretch" spacing={4}>
                <HStack gap={3}>
                  <Box
                    w="12"
                    h="12"
                    borderRadius="xl"
                    bg={plan.color}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon
                      as={iconMap[plan.icon]}
                      boxSize={6}
                      color="white"
                      aria-hidden="true"
                    />
                  </Box>
                  <Box>
                    <Heading size="sm" color={headingColor}>
                      {plan.title}
                    </Heading>
                    <Text fontSize="xs" color={mutedText}>
                      {plan.tagline}
                    </Text>
                  </Box>
                </HStack>

                <Text fontSize="sm" color={textColor}>
                  {plan.description}
                </Text>

                {/* Price */}
                <Box textAlign="center" py={3}>
                  <HStack justify="center" align="baseline" gap={1}>
                    <Text
                      fontSize="3xl"
                      fontWeight="extrabold"
                      color={headingColor}
                    >
                      ${getPrice(plan)}
                    </Text>
                    <Text color={mutedText} fontSize="sm">
                      /mo
                    </Text>
                  </HStack>
                  {isAnnual && (
                    <Text fontSize="xs" color={savingsColor}>
                      ${getTotalPrice(plan)}/year
                    </Text>
                  )}
                </Box>

                <Separator />

                <VStack align="stretch" spacing={2}>
                  {plan.features.map((f, i) => (
                    <HStack key={i} gap={2} align="start">
                      <Icon
                        as={FaCheckCircle}
                        color="green.400"
                        boxSize={3}
                        mt={1}
                        aria-hidden="true"
                        flexShrink={0}
                      />
                      <Text fontSize="xs" color={textColor}>
                        {f}
                      </Text>
                    </HStack>
                  ))}
                </VStack>

                <Button
                  size="lg"
                  width="100%"
                  colorScheme={plan.popular ? "purple" : "teal"}
                  onClick={() => {
                    playSound(navigationSound);
                    onSelectPlan?.(plan.id);
                  }}
                  aria-label={`Subscribe to ${plan.title}`}
                >
                  Start Mentoring
                </Button>
              </VStack>
            </Box>
          </Box>
        ))}
      </Grid>
    </VStack>
  );

  // ────────────────────── Render: Groups & Districts ─────────────
  const renderGroups = () => (
    <VStack spacing={8} width="100%" align="stretch">
      <Box textAlign="center">
        <Heading size="lg" color={headingColor} mb={2}>
          {groupRates.title}
        </Heading>
        <Text color={textColor} maxW="700px" mx="auto">
          {groupRates.description}
        </Text>
      </Box>

      {/* Benefits */}
      <Box
        bg={groupBg}
        borderWidth="1px"
        borderColor={groupBorder}
        borderRadius="xl"
        p={6}
        maxW="800px"
        mx="auto"
        width="100%"
      >
        <Heading size="sm" color={headingColor} mb={4}>
          <Icon as={FaSchool} mr={2} aria-hidden="true" />
          Why Districts Choose Visionary Code:
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
          {groupRates.benefits.map((b, i) => (
            <HStack key={i} gap={2} align="start">
              <Icon
                as={FaCheckCircle}
                color="blue.400"
                boxSize={4}
                mt={1}
                aria-hidden="true"
                flexShrink={0}
              />
              <Text fontSize="sm" color={textColor}>
                {b}
              </Text>
            </HStack>
          ))}
        </SimpleGrid>
      </Box>

      {/* Tiers */}
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }}
        gap={5}
        width="100%"
      >
        {groupRates.tiers.map((tier) => (
          <Box
            key={tier.id}
            bg={cardBg}
            borderRadius="xl"
            borderWidth="2px"
            borderColor={borderColor}
            p={6}
            transition="all 0.3s ease"
            _hover={{ transform: "translateY(-3px)", boxShadow: "lg" }}
            role="article"
            aria-label={`${tier.label} - ${tier.seats} - ${tier.discount} discount`}
          >
            <VStack align="stretch" spacing={3}>
              <Badge
                colorScheme="blue"
                fontSize="xs"
                px={2}
                py={1}
                borderRadius="full"
                width="fit-content"
              >
                {tier.discount}
              </Badge>
              <Heading size="sm" color={headingColor}>
                {tier.label}
              </Heading>
              <Text fontSize="sm" color={mutedText} fontWeight="bold">
                {tier.seats}
              </Text>
              <Text fontSize="xs" color={textColor}>
                {tier.description}
              </Text>
              {tier.perSeatMonthly ? (
                <Box textAlign="center" py={2}>
                  <Text
                    fontSize="2xl"
                    fontWeight="extrabold"
                    color={headingColor}
                  >
                    ${tier.perSeatMonthly}
                  </Text>
                  <Text fontSize="xs" color={mutedText}>
                    per seat/month
                  </Text>
                </Box>
              ) : (
                <Box textAlign="center" py={2}>
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    color="purple.400"
                  >
                    Custom Pricing
                  </Text>
                </Box>
              )}
              <Button
                size="sm"
                width="100%"
                colorScheme={tier.contactSales ? "purple" : "blue"}
                variant={tier.contactSales ? "solid" : "outline"}
                leftIcon={tier.contactSales ? <FaEnvelope /> : <FaUsers />}
                onClick={() => {
                  playSound(navigationSound);
                  if (tier.contactSales) {
                    window.open("mailto:sales@visionarycode.org", "_blank");
                  } else {
                    onSelectPlan?.(tier.id);
                  }
                }}
                aria-label={
                  tier.contactSales
                    ? "Contact sales for custom pricing"
                    : `Select ${tier.label}`
                }
              >
                {tier.contactSales ? "Contact Sales" : "Select Plan"}
              </Button>
            </VStack>
          </Box>
        ))}
      </Grid>

      {/* Selling Points */}
      <SimpleGrid columns={{ base: 1, md: 3 }} gap={6} mt={4}>
        {groupRates.sellingPoints.map((sp, i) => (
          <Box
            key={i}
            bg={cardBg}
            borderRadius="lg"
            p={5}
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Heading size="xs" color={headingColor} mb={2}>
              {sp.title}
            </Heading>
            <Text fontSize="xs" color={textColor}>
              {sp.description}
            </Text>
          </Box>
        ))}
      </SimpleGrid>
    </VStack>
  );

  // ────────────────────── Render: Scholarships ───────────────────
  const renderScholarships = () => (
    <VStack spacing={8} width="100%" align="stretch" maxW="900px" mx="auto">
      <Box textAlign="center">
        <Icon
          as={FaHandHoldingHeart}
          boxSize={12}
          color="orange.400"
          mb={3}
          aria-hidden="true"
        />
        <Heading size="lg" color={headingColor} mb={2}>
          {scholarshipProgram.title}
        </Heading>
        <Text color={textColor} maxW="650px" mx="auto">
          {scholarshipProgram.description}
        </Text>
      </Box>

      {/* Scholarship Tiers */}
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
        gap={6}
      >
        {scholarshipProgram.tiers.map((tier) => (
          <Box
            key={tier.id}
            bg={scholarshipBg}
            borderWidth="2px"
            borderColor={scholarshipBorder}
            borderRadius="xl"
            p={6}
            role="article"
            aria-label={`${tier.label} - ${tier.discount}`}
          >
            <VStack align="stretch" spacing={3}>
              <Badge
                colorScheme="orange"
                fontSize="md"
                px={3}
                py={1}
                borderRadius="full"
                width="fit-content"
                fontWeight="bold"
              >
                {tier.discount}
              </Badge>
              <Heading size="sm" color={headingColor}>
                {tier.label}
              </Heading>
              <Text
                fontSize="sm"
                fontWeight="bold"
                color={headingColor}
                mt={2}
              >
                Eligibility:
              </Text>
              {tier.eligibility.map((e, i) => (
                <HStack key={i} gap={2} align="start">
                  <Icon
                    as={FaCheckCircle}
                    color="orange.400"
                    boxSize={3}
                    mt={1}
                    aria-hidden="true"
                    flexShrink={0}
                  />
                  <Text fontSize="sm" color={textColor}>
                    {e}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        ))}
      </Grid>

      {/* Application Process */}
      <Box
        bg={cardBg}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={borderColor}
        p={6}
      >
        <Heading size="sm" color={headingColor} mb={4}>
          How to Apply:
        </Heading>
        <VStack align="stretch" spacing={3}>
          {scholarshipProgram.applicationProcess.map((step, i) => (
            <HStack key={i} gap={3} align="start">
              <Box
                w="7"
                h="7"
                borderRadius="full"
                bg="orange.400"
                color="white"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight="bold"
                fontSize="sm"
                flexShrink={0}
                aria-hidden="true"
              >
                {i + 1}
              </Box>
              <Text fontSize="sm" color={textColor} pt={1}>
                {step}
              </Text>
            </HStack>
          ))}
        </VStack>
      </Box>

      {/* Impact */}
      <Box textAlign="center" py={4}>
        <Text
          fontSize="md"
          fontStyle="italic"
          color={mutedText}
          maxW="500px"
          mx="auto"
        >
          "{scholarshipProgram.impactStatement}"
        </Text>
      </Box>

      <Button
        size="lg"
        mx="auto"
        colorScheme="orange"
        width={{ base: "100%", md: "auto" }}
        px={10}
        leftIcon={<FaGraduationCap />}
        onClick={() => {
          playSound(navigationSound);
          window.open("mailto:scholarships@visionarycode.org", "_blank");
        }}
        aria-label="Apply for a scholarship"
      >
        Apply for Scholarship
      </Button>
    </VStack>
  );

  // ──────────────── Active Tab Content ────────────────────────────
  const renderTabContent = () => {
    switch (activeTab) {
      case "bundles":
        return renderBundles();
      case "individual":
        return renderIndividual();
      case "mentoring":
        return renderMentoring();
      case "groups":
        return renderGroups();
      case "scholarships":
        return renderScholarships();
      default:
        return renderBundles();
    }
  };

  return (
    <Box
      p={{ base: 4, md: 8 }}
      bg={screenBg}
      minHeight="100vh"
      width="100%"
    >
      <VStack spacing={8} maxW="1200px" mx="auto">
        {/* ─── Page Heading ──── */}
        <Box textAlign="center" width="100%">
          <Heading
            ref={headingRef}
            tabIndex={-1}
            size="2xl"
            bgGradient={accentGradient}
            bgClip="text"
            mb={3}
            id="pricing-heading"
          >
            Plans & Pricing
          </Heading>
          <Text fontSize="lg" color={textColor} maxW="700px" mx="auto">
            Accessible coding education for every learner. Choose a plan that
            fits your needs — from individual courses to district-wide
            deployments.
          </Text>
        </Box>

        {/* ─── Annual / Monthly Toggle ──── */}
        <HStack
          bg={tabBg}
          borderRadius="full"
          p={2}
          gap={3}
          role="group"
          aria-label="Billing cycle selector"
        >
          <Text
            fontSize="sm"
            fontWeight={!isAnnual ? "bold" : "normal"}
            color={!isAnnual ? headingColor : mutedText}
          >
            Monthly
          </Text>
          <Switch.Root
            checked={isAnnual}
            onCheckedChange={(e) => {
              setIsAnnual(e.checked);
              playSound(navigationSound);
            }}
            aria-label="Toggle annual billing. Annual billing saves up to 17%."
            colorPalette="purple"
            size="lg"
          >
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Root>
          <Text
            fontSize="sm"
            fontWeight={isAnnual ? "bold" : "normal"}
            color={isAnnual ? headingColor : mutedText}
          >
            Annual
          </Text>
          {isAnnual && (
            <Badge
              colorScheme="green"
              fontSize="xs"
              borderRadius="full"
              px={2}
            >
              Save up to 17%
            </Badge>
          )}
        </HStack>

        {/* ─── Tab Navigation ──── */}
        <Flex
          role="tablist"
          aria-label="Pricing categories"
          gap={2}
          flexWrap="wrap"
          justify="center"
          bg={tabBg}
          borderRadius="xl"
          p={2}
          width="100%"
          maxW="800px"
        >
          {tabs.map((tab, idx) => (
            <Button
              key={tab.id}
              id={`pricing-tab-${tab.id}`}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`pricing-panel-${tab.id}`}
              tabIndex={activeTab === tab.id ? 0 : -1}
              onKeyDown={(e) => handleTabKeyDown(e, idx)}
              onClick={() => {
                setActiveTab(tab.id);
                playSound(navigationSound);
              }}
              bg={activeTab === tab.id ? tabActiveBg : "transparent"}
              boxShadow={activeTab === tab.id ? "md" : "none"}
              fontWeight={activeTab === tab.id ? "bold" : "normal"}
              color={activeTab === tab.id ? headingColor : mutedText}
              borderRadius="lg"
              size="sm"
              px={4}
              transition="all 0.2s"
              _hover={{
                bg: activeTab === tab.id ? tabActiveBg : "whiteAlpha.300",
              }}
              leftIcon={<Icon as={tab.icon} aria-hidden="true" />}
            >
              {tab.label}
            </Button>
          ))}
        </Flex>

        {/* ─── Tab Panel ──── */}
        <Box
          id={`pricing-panel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`pricing-tab-${activeTab}`}
          width="100%"
        >
          {renderTabContent()}
        </Box>

        {/* ─── Back Button ──── */}
        <HStack spacing={4} justify="center" width="100%" pt={4}>
          <Button
            onClick={() => {
              playSound(navigationSound);
              onBack?.();
            }}
            variant="outline"
            size="lg"
            aria-label="Go back to previous screen"
          >
            Go Back
          </Button>
        </HStack>

        {/* ─── Screen Reader Live Region ──── */}
        <Box
          className="sr-only"
          role="region"
          aria-live="polite"
          aria-atomic="true"
        >
          <Text>
            Currently viewing {tabs.find((t) => t.id === activeTab)?.label}{" "}
            pricing. Billing cycle:{" "}
            {isAnnual ? "Annual (save up to 17%)" : "Monthly"}.
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};
